import { parseFactId } from '../engine/facts';
import type { FactId } from '../engine/facts';
import type { Summary } from './useTrainer';
import { duration, expr, percent, seconds } from './format';
import { Hint, PrimaryButton, Screen, SecondaryButton, Title } from './kit';

interface Props {
  summary: Summary;
  onRepeat: () => void;
  onFact: (factId: FactId) => void;
  onDone: () => void;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-line bg-card p-3">
      <dt className="text-[0.85rem] text-muted">{label}</dt>
      <dd className="m-0 mt-1 text-[1.35rem] font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

/** «Какие именно» из раздела 6.1: одних количеств спека не считает
 *  достаточным. Каждый пример ведёт на свою статистику. */
function FactChips({
  factIds,
  tone,
  onFact,
}: {
  factIds: FactId[];
  tone: string;
  onFact: (factId: FactId) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
      {factIds.map((factId) => (
        <li key={factId}>
          <button
            type="button"
            onClick={() => onFact(factId)}
            /** Не меньше 44 пикселей: чипы стояли по 26 при зазоре в шесть,
             *  и ребёнок, метясь в «6 × 7», попадал в соседний пример и
             *  уходил на чужую статистику. */
            className={`flex min-h-11 items-center rounded-lg border px-3 py-2 text-sm font-semibold tabular-nums ${tone}`}
          >
            {expr(parseFactId(factId))}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ResultsScreen({ summary, onRepeat, onFact, onDone }: Props) {
  const { totals, wallClockMs, gained, lost, erroredTaskIds, deltaKnown } = summary;

  return (
    <Screen>
      <Title>Готово</Title>

      <dl className="m-0 grid grid-cols-2 gap-2">
        <Stat label="Заданий" value={totals.taskCount} />
        <Stat label="Попыток" value={totals.totalAttempts} />
        <Stat label="Ошибок" value={totals.wrongAttempts} />
        <Stat label="Точность" value={percent(totals.accuracy)} />
        <Stat label="Время" value={duration(wallClockMs)} />
        <Stat label="Средний ответ" value={seconds(totals.avgAnswerMs)} />
        {totals.assistedTasks > 0 && <Stat label="С подсказкой" value={totals.assistedTasks} />}
      </dl>

      <div className="grid gap-2">
        {gained.length > 0 && (
          <div className="grid gap-1.5">
            <p className="m-0 font-semibold text-correct">Освоено новых: {gained.length}</p>
            <FactChips
              factIds={gained}
              tone="border-correct bg-correct-soft text-correct"
              onFact={onFact}
            />
          </div>
        )}
        {lost.length > 0 && (
          <div className="grid gap-1.5">
            <p className="m-0 font-semibold text-incorrect">Вернулось в работу: {lost.length}</p>
            <FactChips
              factIds={lost}
              tone="border-incorrect bg-incorrect-soft text-incorrect"
              onFact={onFact}
            />
          </div>
        )}
        {gained.length === 0 && lost.length === 0 && (
          <Hint>
            {deltaKnown
              ? 'Освоенность не изменилась — так бывает, продолжай'
              : 'Тренировка записана, а прирост освоенности не удалось посчитать'}
          </Hint>
        )}
      </div>

      {/* Кнопка не показывается, если ошибок не было (§11). */}
      {erroredTaskIds.length > 0 && (
        <SecondaryButton bottom onClick={onRepeat}>
          Повторить ошибки ({erroredTaskIds.length})
        </SecondaryButton>
      )}

      <PrimaryButton bottom={erroredTaskIds.length === 0} onClick={onDone}>
        Готово
      </PrimaryButton>
    </Screen>
  );
}
